import React, { useState } from 'react';
import './AdminLogin.css';
import { useAuth } from '../contexts/AuthContext';

interface AdminLoginProps {
    onClose: () => void;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onClose }) => {
    const { signIn, requestPasswordReset } = useAuth();
    const [mode, setMode] = useState<'login' | 'reset'>('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const switchMode = (next: 'login' | 'reset') => {
        setMode(next);
        setError(null);
        setInfo(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        const errorMsg = await signIn(email, password);
        setLoading(false);
        if (errorMsg) {
            setError('Invalid email or password.');
        } else {
            onClose();
        }
    };

    const handleResetSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setInfo(null);
        setLoading(true);
        // Ignore the result: always show a neutral message so we don't reveal
        // whether an account exists for the given email.
        await requestPasswordReset(email);
        setLoading(false);
        setInfo('If an account exists for that email, a reset link is on its way.');
    };

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="admin-login-backdrop" onClick={handleBackdropClick} role="dialog" aria-modal="true" aria-labelledby="admin-login-title">
            <div className="admin-login-modal">
                <button className="admin-login-close" onClick={onClose} aria-label="Close login">×</button>
                {mode === 'login' ? (
                    <>
                        <h2 id="admin-login-title" className="admin-login-title">Admin Login</h2>
                        <form onSubmit={handleSubmit} className="admin-login-form">
                            <label className="admin-login-label" htmlFor="admin-email">Email</label>
                            <input
                                id="admin-email"
                                className="admin-login-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                                autoFocus
                            />
                            <label className="admin-login-label" htmlFor="admin-password">Password</label>
                            <input
                                id="admin-password"
                                className="admin-login-input"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                            />
                            {error && <p className="admin-login-error">{error}</p>}
                            <button className="admin-login-submit" type="submit" disabled={loading}>
                                {loading ? 'Signing in…' : 'Sign in'}
                            </button>
                            <button className="admin-login-link" type="button" onClick={() => switchMode('reset')}>
                                Forgot password?
                            </button>
                        </form>
                    </>
                ) : (
                    <>
                        <h2 id="admin-login-title" className="admin-login-title">Reset Password</h2>
                        <form onSubmit={handleResetSubmit} className="admin-login-form">
                            <label className="admin-login-label" htmlFor="admin-reset-email">Email</label>
                            <input
                                id="admin-reset-email"
                                className="admin-login-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                                autoFocus
                            />
                            {info && <p className="admin-login-info">{info}</p>}
                            {error && <p className="admin-login-error">{error}</p>}
                            <button className="admin-login-submit" type="submit" disabled={loading}>
                                {loading ? 'Sending…' : 'Send reset link'}
                            </button>
                            <button className="admin-login-link" type="button" onClick={() => switchMode('login')}>
                                Back to login
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminLogin;
